import { useFeatures } from '@shared/feature-flags';
import { MarkdownPreview } from '@shared/markdown';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { LiaTimesSolid } from 'react-icons/lia';

import { Date, Timestamp } from '@/components/date-time';
import Nullish from '@/components/nullish';
import { Button } from '@/components-new/button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '@/components-new/description-list';
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from '@/components-new/drawer';
import { SectionHeading } from '@/components-new/section';
import { TextLink } from '@/components-new/text';
import { PdlStatus } from '@/features/coverage/types/pdl-status';
import { PdlStatusBadge } from '@/features/drugs/components/pdl-status-badge';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useDelayedClose } from '@/hooks/use-delayed-close';
import { automatedPaDisplay, stepTherapyDisplay } from '@/utils/state-coverage';

export type CoverageDetails = {
  state: { label: string };
  drug?: { label: string };
  pdlStatus?: PdlStatus;
  pdlStatusDate?: string | null;
  pdlStatusEffectiveDate?: string | null;
  pdlLink?: string;
  paTypes?: Array<{ id: number; label: string }>;
  stepTherapyCount?: number | null;
  hasAdditionalSteps?: boolean | null;
  paCriteriaDescription?: string | null;
  paCriteriaSourceUrl?: string | null;
  hasAutoPa: boolean;
  updatedAt?: string;
};

type CoverageDetailsDrawerProps = {
  coverage?: CoverageDetails;
  onClose: () => void;
}

export const CoverageDetailsDrawer = ({ coverage, onClose }: CoverageDetailsDrawerProps) => {
  const { isOpen, handleOpen, handleClose } = useDelayedClose(!!coverage, onClose);
  const { getFeature } = useFeatures();
  const { hasPolicies } = useCurrentUser();

  const isStepTherapyEnabled = getFeature('clientViewStepTherapy');
  const isInternalUser = hasPolicies(['isAnyArtiaUser']);
  const showStepTherapy = isInternalUser || isStepTherapyEnabled;

  useEffect(() => {
    if (coverage) {
      handleOpen();
    }
  }, [coverage, handleOpen]);

  return (
    <Drawer open={isOpen} onClose={handleClose} size="3xl">
      {!!coverage && (
        <>
          <DrawerHeader>
            <DrawerTitle>
              Coverage Details
              {coverage.updatedAt && (
                <span className="px-1 text-sm font-normal text-gray-500">
                  (Last updated at <Timestamp value={coverage.updatedAt}/>)
                </span>
              )}
            </DrawerTitle>
            <Button plain onClick={handleClose}>
              <LiaTimesSolid/>
            </Button>
          </DrawerHeader>
          <DrawerBody>
            <div className="space-y-6">
              <div>
                <SectionHeading level={3}>General</SectionHeading>
                <DescriptionList>
                  <DescriptionTerm>State</DescriptionTerm>
                  <DescriptionDetails>{coverage.state.label}</DescriptionDetails>

                  {coverage.drug && (
                    <>
                      <DescriptionTerm>Drug</DescriptionTerm>
                      <DescriptionDetails>{coverage.drug.label}</DescriptionDetails>
                    </>
                  )}

                  <DescriptionTerm>Status Change Effective Date</DescriptionTerm>
                  <DescriptionDetails>
                    <Nullish
                      value={coverage.pdlStatusEffectiveDate}
                      valueRenderer={(date) => <Date value={date!}/>}
                    />
                  </DescriptionDetails>

                  {coverage.pdlLink !== undefined && (
                    <>
                      <DescriptionTerm>PDL Link</DescriptionTerm>
                      <DescriptionDetails>
                        <Nullish
                          value={coverage.pdlLink}
                          valueRenderer={(link) => <TextLink href={link}>{link}</TextLink>}
                        />
                      </DescriptionDetails>
                    </>
                  )}

                  <DescriptionTerm>PDL Status</DescriptionTerm>
                  <DescriptionDetails>
                    <PdlStatusBadge pdlStatus={coverage.pdlStatus}/>
                  </DescriptionDetails>

                  <DescriptionTerm>PDL Status Date</DescriptionTerm>
                  <DescriptionDetails>
                    <Nullish
                      value={coverage.pdlStatusDate}
                      valueRenderer={(date) => <Date value={date!}/>}
                    />
                  </DescriptionDetails>
                </DescriptionList>
              </div>

              <div>
                <SectionHeading level={3}>Prior Authorization</SectionHeading>
                <DescriptionList>
                  <DescriptionTerm>Type</DescriptionTerm>
                  <DescriptionDetails>
                    <Nullish value={coverage.paTypes?.map(type => type.label).join('; ')}/>
                  </DescriptionDetails>

                  {showStepTherapy && (
                    <>
                      <DescriptionTerm>Step Therapy</DescriptionTerm>
                      <DescriptionDetails>
                        <Nullish value={stepTherapyDisplay(coverage.stepTherapyCount ?? undefined, coverage.hasAdditionalSteps ?? undefined)}/>
                      </DescriptionDetails>
                    </>
                  )}

                  <DescriptionTerm>Source Link</DescriptionTerm>
                  <DescriptionDetails>
                    <Nullish
                      value={coverage.paCriteriaSourceUrl}
                      valueRenderer={(link) => (
                        <TextLink
                          href={link!}
                          target="_blank"
                          referrerPolicy="no-referrer"
                          className="break-all"
                        >
                          {link}
                        </TextLink>
                      )}
                    />
                  </DescriptionDetails>

                  <DescriptionTerm>Automated</DescriptionTerm>
                  <DescriptionDetails>
                    {automatedPaDisplay(coverage.hasAutoPa)}
                  </DescriptionDetails>
                </DescriptionList>
              </div>

              <div>
                <SectionHeading level={3} className="mb-3">Prior Authorization Criteria</SectionHeading>
                {isEmpty(coverage.paCriteriaDescription)
                  ? (<span className="text-sm text-gray-500">Additional criteria not specified.</span>)
                  : (<MarkdownPreview maxHeight="100%" markdown={coverage.paCriteriaDescription ?? ''}/>)
                }
              </div>
            </div>
          </DrawerBody>
        </>
      )}
    </Drawer>
  );
};
